import React, { useMemo } from 'react'

import { Stack, Typography, useTheme } from '@mui/material'
import { LifeBuoyIcon, LockIcon } from 'lucide-react'

import { Card } from '@repo/ui'
import { EmptyState, Table, StatusChipCell, LinkCell } from 'components'
import { SUPPORT_REQUEST_STATUSES_INTERNAL } from 'constants/statuses'
import { useCompanySupportRequests } from 'hooks/api'
import { ROUTES } from 'routes'
import { relativeOrDateTime } from 'utils'
import type { SupportRequest } from 'services/supportApi'

/**
 * One company's support queue — the Support tab of the internal company detail page
 * (VRG-516, against the endpoint VRG-515 built).
 *
 * ## What this shows that the brand's own queue does not
 *
 * The FULL internal queue, including automatically-filed crash reports the submitter
 * never claimed. The customer-facing list hides those (correct for a brand looking at
 * itself); staff asking "how much trouble is this company in" are precisely the
 * audience for them, so hiding them here would make the tab under-report.
 *
 * ## The status counts are a SNAPSHOT, and the copy says so
 *
 * `support_requests` has no status-history table — a request moving `open → resolved`
 * overwrites the column — so the summary is the state right now and cannot be trended.
 * That is why this renders counts and not a "resolved per week" chart: the data to draw
 * one does not exist, and a chart built on `created_at` while labelled with status
 * would be a plausible-looking lie. Volume over `created_at` WOULD be sound (it is
 * immutable), and is deliberately left for when someone asks for it rather than
 * invented here.
 */

interface CompanySupportPanelProps {
  companyId: string
}

export const CompanySupportPanel = ({ companyId }: CompanySupportPanelProps) => {
  const theme = useTheme()
  const { data, isLoading, error } = useCompanySupportRequests(companyId)

  const rows = useMemo(
    () =>
      (data?.items ?? []).map((request: SupportRequest) => ({
        id: request.id,
        reference: request.reference,
        type: request.type,
        status: request.status,
        submitter: request.submitterName || request.submitterEmail,
        body: request.body,
        created_at: request.createdAt,
      })),
    [data],
  )

  const columns = useMemo(
    () => [
      {
        field: 'reference',
        // "ID", never "Ref" — user directive, and this column ESTABLISHES the label:
        // nothing in the platform surfaced `support_requests.reference` before, so
        // there is no existing wording to be consistent with, only a first one to get
        // right. One label for one concept (§44's argument, applied to a header).
        headerName: 'ID',
        type: 'number',
        // Sized for the HEADER, not the digits (§49). "ID" is two characters, but a
        // sortable header also carries ~18px of sort glyph plus the grid's cell
        // padding — and a number column sized to its values is the exact recurring
        // mistake §49 was written from ("Inve…" at width 100).
        width: 110,
        // A real click-through to the request, not dead text. `LinkCell` calls
        // `useReferrerState` itself, so the detail page gets its "back to" for free
        // (§42) — this is a cross-surface jump the breadcrumb cannot describe, since
        // the request lives under /internal/support, not under this company.
        renderCell: ({ row }: { row: { id: string; reference: number } }) => (
          <LinkCell
            align="left"
            route={ROUTES.INTERNAL_SUPPORT_REQUEST(row.id)}
            link={`#${row.reference}`}
          />
        ),
      },
      {
        field: 'type',
        headerName: 'Type',
        type: 'string',
        // Sized for the HEADER plus its sort icon, not for "bug" (§49).
        width: 120,
        renderCell: ({ value }: { value: string }) => (
          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {value}
          </Typography>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        width: 180,
        renderCell: ({ value }: { value: string }) =>
          SUPPORT_REQUEST_STATUSES_INTERNAL[
            value as keyof typeof SUPPORT_REQUEST_STATUSES_INTERNAL
          ] ? (
            <StatusChipCell status={value} statuses={SUPPORT_REQUEST_STATUSES_INTERNAL} />
          ) : (
            // A status the constants map does not know still renders as ITSELF rather
            // than as blank — an unmapped value is a wiring gap worth seeing, and a
            // blank cell would hide it.
            <Typography variant="body2">{value}</Typography>
          ),
      },
      {
        field: 'submitter',
        headerName: 'Submitted by',
        type: 'string',
        width: 220,
        renderCell: ({ value }: { value: string | null }) => (
          <Typography variant="body2" color={value ? 'text.primary' : 'text.secondary'}>
            {/* Unclaimed auto-filed reports genuinely have no submitter. Naming that is
                more useful here than a dash, because "nobody reported this, the app
                did" is the distinguishing fact about those rows. */}
            {value || 'Auto-filed'}
          </Typography>
        ),
      },
      {
        field: 'body',
        headerName: 'Request',
        type: 'string',
        flex: 1,
        minWidth: 260,
      },
      {
        field: 'created_at',
        headerName: 'Created',
        type: 'string',
        width: 180,
        renderCell: ({ value }: { value: string | null | undefined }) =>
          value ? relativeOrDateTime(value) : '—',
      },
    ],
    [],
  )

  if (error) {
    const status = (error as Error & { status?: number }).status
    return (
      <Card>
        <EmptyState
          icon={
            <LockIcon size={28} strokeWidth={1} color={theme.palette.text.secondary} />
          }
          title={
            status === 403
              ? 'You cannot view support requests'
              : 'Could not load support requests'
          }
          text={
            status === 403
              ? 'Support requests need the "Manage internal support" permission. Ask a platform admin to grant it.'
              : 'The request failed. Reload the page, or check the API is running.'
          }
          sx={{ py: 10 }}
        />
      </Card>
    )
  }

  const summary = data?.summary

  return (
    <Stack spacing={4}>
      {/* Counts first, and only when there is something to count — a row of zeroes
          above an empty table says the same thing twice. */}
      {!isLoading && summary && summary.total > 0 && (
        <Stack direction="row" spacing={5} flexWrap="wrap" alignItems="baseline">
          <Typography variant="body2">
            {summary.total} {summary.total === 1 ? 'request' : 'requests'}
          </Typography>
          {Object.entries(summary.byStatus).map(([status, count]) => (
            <Typography key={status} variant="body3" color="text.secondary">
              {SUPPORT_REQUEST_STATUSES_INTERNAL[
                status as keyof typeof SUPPORT_REQUEST_STATUSES_INTERNAL
              ]?.label ?? status}
              : {count}
            </Typography>
          ))}
          <Typography variant="body3" color="text.secondary">
            Status counts are current, not history.
          </Typography>
        </Stack>
      )}

      {/* No `fillBottom` (§22): this is a tabbed panel, and the summary row above it
          plus the page chrome mean a viewport-filling grid would push its own last
          rows off-screen. Same fixed `topOffset` as the sibling Orders/Invoices tabs. */}
      <Table
        loading={isLoading}
        topOffset={420}
        columns={columns}
        rows={rows}
        emptyState={
          <EmptyState
            title="No support requests"
            text="This company has not filed a support request, and nothing has been auto-filed for it."
            icon={<LifeBuoyIcon strokeWidth={1} size={32} />}
          />
        }
      />
    </Stack>
  )
}
